import { createFileRoute, Link } from "@tanstack/react-router";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/business/")({
  component: BusinessPage,
});

function BusinessPage() {
  return (
    <div className="page-enter bp-utility-page bp-business-page">
      <SEOHead
        title="Business — Bulk Custom Merch"
        description="Custom merch for your team, event, or brand. Volume pricing, a dedicated rep, and reliable fulfillment from Quuil."
        path="/business"
        keywords={["bulk custom merch", "company swag", "team apparel", "event merchandise", "custom business merch"]}
      />

      {/* Hero */}
      <section className="bg-qds-background" aria-labelledby="business-hero-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-qds-surface border border-qds-border rounded-full px-4 py-1.5 mb-6">
              <span className="text-xs font-medium text-qds-text-secondary">For Teams &amp; Businesses</span>
            </div>

            <h1 id="business-hero-heading" className="text-3xl md:text-5xl font-light tracking-tight text-qds-text-primary mb-5 leading-[1.1]">
              Your Brand,{" "}
              <span className="font-medium">on Everything</span>
            </h1>
            <p className="text-lg text-qds-text-secondary mb-8 leading-relaxed max-w-lg">
              Custom tees, hoodies, and merch for your team, your event,
              or your storefront. Volume pricing and a real person handling your order.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/business/quote"
                className="inline-flex items-center justify-center bg-qds-primary text-qds-text-inverse px-7 py-3.5 rounded-xl font-medium text-base hover:bg-qds-primary-pressed transition-colors min-h-[48px]"
              >
                Get a quote
              </Link>
              <Link
                to="/business/catalog"
                className="inline-flex items-center justify-center border border-qds-border px-7 py-3.5 rounded-xl font-medium text-base text-qds-text-primary hover:bg-qds-surface-hover transition-colors min-h-[48px]"
              >
                Browse the catalog
              </Link>
              <Link
                to="/business/portal"
                className="inline-flex items-center justify-center border border-qds-border px-7 py-3.5 rounded-xl font-medium text-base text-qds-text-primary hover:bg-qds-surface-hover transition-colors min-h-[48px]"
              >
                Open portal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="border-y border-qds-border bg-qds-surface" aria-label="Trust indicators">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-qds-text-secondary">
            <span className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Payments secured by Stripe
            </span>
            <span className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Quotes back in 24 hours
            </span>
            <span className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Quality you can feel
            </span>
            <span className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              Free shipping at 100+ units
            </span>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="bg-qds-background" aria-labelledby="value-props-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <h2 id="value-props-heading" className="sr-only">Why choose Quuil for business</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Better Price at Scale",
                description: "Order more, pay less per piece. Tiered pricing kicks in at 25 units.",
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                ),
              },
              {
                title: "A Real Person on Your Order",
                description: "Your dedicated rep handles everything from design to delivery. No ticket queues.",
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
                  </svg>
                ),
              },
              {
                title: "Quick Turnaround",
                description: "Standard orders ship in 5-7 business days. Need it sooner? Ask about rush production.",
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.title} className="text-center md:text-left">
                <div className="w-12 h-12 bg-qds-primary/5 rounded-xl flex items-center justify-center mx-auto md:mx-0 mb-4 text-qds-text-primary">
                  {item.icon}
                </div>
                <h3 className="text-lg font-medium text-qds-text-primary mb-2">{item.title}</h3>
                <p className="text-qds-text-secondary text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="bg-qds-surface border-y border-qds-border" aria-labelledby="pricing-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <h2 id="pricing-heading" className="text-2xl md:text-3xl font-medium text-qds-text-primary text-center mb-3">
            Straightforward Pricing
          </h2>
          <p className="text-qds-text-secondary text-center text-sm mb-10 max-w-md mx-auto">
            No hidden fees. Discounts apply automatically based on how many you order.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { range: "1-24 units", discount: "Standard pricing", label: "Starter", highlighted: false },
              { range: "25-99 units", discount: "10% off", label: "Small Team", highlighted: false },
              { range: "100-499 units", discount: "20% off", label: "Company", highlighted: true },
              { range: "500+ units", discount: "Custom pricing", label: "Large Scale", highlighted: false },
            ].map((tier) => (
              <div
                key={tier.range}
                className={`p-6 rounded-xl border transition-colors ${
                  tier.highlighted
                    ? "border-qds-primary bg-qds-primary-tint relative"
                    : "border-qds-border hover:border-qds-border-strong"
                }`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-qds-primary text-qds-text-inverse text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                    Most Picked
                  </span>
                )}
                <span className="text-xs font-medium text-qds-text-tertiary uppercase tracking-wide">
                  {tier.label}
                </span>
                <p className="text-lg font-medium text-qds-text-primary mt-2 mb-1">{tier.discount}</p>
                <p className="text-sm text-qds-text-secondary">{tier.range}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/business/quote"
              className="inline-flex items-center gap-2 bg-qds-primary text-qds-text-inverse px-6 py-3 rounded-xl font-medium text-sm hover:bg-qds-primary-pressed transition-colors min-h-[44px]"
            >
              Get your quote
            </Link>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-qds-background" aria-labelledby="use-cases-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <h2 id="use-cases-heading" className="text-2xl md:text-3xl font-medium text-qds-text-primary text-center mb-10">
            Who It's For
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { title: "Company Events", desc: "Branded merch for offsites, conferences, and team milestones." },
              { title: "Online Stores", desc: "White-label products with your designs. We handle printing and shipping." },
              { title: "Schools & Clubs", desc: "Team gear, club merch, and fundraiser products." },
              { title: "Promotions & Gifts", desc: "Branded giveaways and customer gifts that people actually keep." },
            ].map((item) => (
              <div key={item.title} className="bg-qds-surface border border-qds-border rounded-xl p-6">
                <h3 className="font-medium text-qds-text-primary mb-2">{item.title}</h3>
                <p className="text-sm text-qds-text-secondary leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-qds-surface border-t border-qds-border" aria-labelledby="cta-heading">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <h2 id="cta-heading" className="text-2xl md:text-3xl font-medium text-qds-text-primary mb-3">
            Tell Us What You Need
          </h2>
          <p className="text-qds-text-secondary text-sm mb-8 max-w-md mx-auto">
            Fill out a quick form and we'll send you a quote within 24 hours.
          </p>
          <Link
            to="/business/quote"
            className="inline-flex items-center gap-2 bg-qds-primary text-qds-text-inverse px-8 py-4 rounded-xl font-medium text-base hover:bg-qds-primary-pressed transition-colors min-h-[48px]"
          >
            Get a quote
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}
